import { ChangeEvent, useRef, useState } from "react";
import PageGroup from "../../models/PageGroup";
import PageGroupTreeViewModel from "../../modelTreeViews/PageGroupTreeviewModel";
import { useOnChangeWatcher } from "../../models/useOnChangeWatcher";
import type Page from "../../models/Page";
import type Floor from "../../models/Floor";
import PdfPageReference from "../../models/PdfPageReference";
import { useRedraw } from "../../models/useRerender";
import { itx } from "../../translationItx/itx";
import BaseModel from "../../models/BaseModel";
import { EventKey } from "../../eventLogger/EventKeys";
import { showToast } from "../Toasts";
import { applyDetectedFloors } from "./initData";
import { extractFloorsAndRoomsFromPdf } from "./floorplanExtraction";

// Erkennt aus der hochgeladenen PDF-Datei Etagen/Räume per KI und übernimmt sie
// ins Projekt. Darf den Assistenten bei Fehlern (kein Netz, API-Fehler, ...) nicht
// blockieren – die bisherigen Etagen/Räume bleiben in dem Fall einfach erhalten.
// Gibt zurück, ob die Erkennung erfolgreich durchgelaufen ist (auch wenn dabei
// nichts gefunden wurde) – nur dann macht ein automatisches Weiterspringen Sinn.
async function detectFloorsAndRoomsFromFile(
  file: File,
  pdfDocument: Page | undefined,
  onProgress: (text: string) => void,
): Promise<boolean> {
  try {
    onProgress(itx("loadFloorPlan.openingPdf", "Öffne PDF …"));
    const detectedFloors = await extractFloorsAndRoomsFromPdf(file, (current, total) => {
      onProgress(
        itx("loadFloorPlan.analyzingPage", "Analysiere Grundriss … Seite T1/T2", {
          T1: String(current),
          T2: String(total),
        }),
      );
    });
    onProgress(itx("loadFloorPlan.applyingResults", "Übernehme erkannte Etagen und Räume …"));
    // Jede erkannte Etage bekommt automatisch die PDF-Seite, auf der sie erkannt wurde, als
    // Hintergrundbild zugewiesen (siehe DetectedFloor.pdfPageNumber) - zeigen mehrere Etagen auf
    // derselben Seite, erhalten sie folgerichtig auch dieselbe Seite als Hintergrund.
    await applyDetectedFloors(detectedFloors, pdfDocument);
    if (detectedFloors.length === 0) {
      showToast({
        text: itx(
          "loadFloorPlan.noFloorsDetected",
          "Aus dem Plan konnten keine Etagen/Räume automatisch erkannt werden. Bitte lege sie in den nächsten Schritten manuell an.",
        ),
      });
    }
    return true;
  } catch (err) {
    showToast({
      isError: true,
      text: (err as Error)?.message
        ? (err as Error).message
        : itx(
            "loadFloorPlan.detectionFailed",
            "Die automatische Erkennung von Etagen/Räumen ist fehlgeschlagen. Du kannst sie in den nächsten Schritten manuell anlegen.",
          ),
    });
    return false;
  }
}

function DeleteButton(props: { obj: BaseModel; style?: any }) {
  const { obj, style } = props;
  return (
    <button
      className="iconButton DeleteButton"
      data-tooltip-id="tv-tooltip"
      data-tooltip-content={state.translate("listComponents.deleteT1", "lösche T1", {
        T1: state.translate(`components.${obj.className}`, obj.className),
      })}
      data-tooltip-place="bottom"
      style={{
        position: "absolute",
        right: "10px",
        ...style,
      }}
      onClick={(ev) => {
        ev.stopPropagation();
        obj.delete();
      }} // not working for Floor
    >
      <i className={`las la-trash`}></i>{" "}
    </button>
  );
}

export default function LoadFloorPlans(props: {
  documentPageGroup: PageGroup;
  onAnalysisStart?: () => void;
  onAnalysisProgress?: (text: string) => void;
  onAnalysisComplete?: (success: boolean) => void;
}) {
  const { documentPageGroup, onAnalysisStart, onAnalysisProgress, onAnalysisComplete } = props;

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <BuildingPlanDropzone
        documentPageGroup={documentPageGroup}
        onAnalysisStart={onAnalysisStart}
        onAnalysisProgress={onAnalysisProgress}
        onAnalysisComplete={onAnalysisComplete}
      />
    </div>
  );
}

export function BuildingPlanDropzone(props: {
  documentPageGroup: PageGroup | undefined;
  onAnalysisStart?: () => void;
  onAnalysisProgress?: (text: string) => void;
  onAnalysisComplete?: (success: boolean) => void;
}) {
  const { documentPageGroup, onAnalysisStart, onAnalysisProgress, onAnalysisComplete } = props;
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  async function handleFiles(files: FileList | null) {
    if (!documentPageGroup || !files || files.length === 0) {
      return;
    }
    const fakeEvent = { target: { files } } as unknown as ChangeEvent<HTMLInputElement>;
    const pdfDocument = await (documentPageGroup.treeViewModel as PageGroupTreeViewModel).createPageFromPdfFile(
      fakeEvent,
    );
    state.eventLogger.addEvent(EventKey.BackgroundPdfUploaded, {
      pageGroupId: documentPageGroup.id,
    });

    // Wie beim Anlegen der Seite (siehe createPageFromPdfFile) wird nur die erste Datei berücksichtigt.
    const pdfFile = Array.from(files).find((f) => f.type === "application/pdf");
    if (!pdfFile) {
      return;
    }

    // KI-Erkennung von Etagen/Räumen aus dem hochgeladenen Plan (nur im Assistenten relevant,
    // da BuildingPlanDropzone ausschließlich dort verwendet wird).
    if (onAnalysisStart) {
      onAnalysisStart();
      detectFloorsAndRoomsFromFile(pdfFile, pdfDocument, (text) => onAnalysisProgress?.(text)).then((success) => {
        onAnalysisComplete?.(success);
      });
    }
  }

  return (
    <div
      onClick={() => inputRef.current?.click()}
      onDragOver={(ev) => {
        ev.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={(ev) => {
        ev.preventDefault();
        setIsDragging(false);
        handleFiles(ev.dataTransfer.files);
      }}
      style={{
        width: "100%",
        maxWidth: "500px",
        boxSizing: "border-box",
        padding: "30px 20px",
        borderRadius: "12px",
        border: `2px dashed ${isDragging ? "#ffffff" : "rgba(255, 255, 255, 0.5)"}`,
        background: isDragging
          ? "rgba(255, 255, 255, 0.25)" // no itx
          : "rgba(255, 255, 255, 0.08)", // no itx
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "8px",
        cursor: "pointer",
        transition: "background 0.15s ease, border-color 0.15s ease",
      }}
    >
      <i className="las la-cloud-upload-alt" style={{ fontSize: "40px", color: "#ffffff" }} />
      <div style={{ fontWeight: 700, color: "#ffffff" }}>
        {itx("loadFloorPlan.dropzoneTitle", "Gebäudeplan hierher ziehen")}
      </div>
      <div style={{ fontSize: "0.8rem", color: "rgba(255, 255, 255, 0.8)" }}>
        {itx("loadFloorPlan.dropzoneSubtitle", "oder klicken, um eine PDF-Datei auszuwählen")}
      </div>
      <input
        ref={inputRef}
        id="file" // no itx
        name="file" // no itx
        type="file"
        accept=".pdf"
        style={{ display: "none" }}
        onClick={(ev) => ev.stopPropagation()}
        onChange={(ev) => handleFiles(ev.target.files)}
      />
    </div>
  );
}

export function BuildingPlanList(props: {
  onChange: () => void;
  documentPageGroup: PageGroup;
  selectedElem?: ({ page, pageNumber }) => void;
  defaultSelectedPage?: { page: Page | undefined; pageNumber: number };
}) {
  const { onChange, documentPageGroup, selectedElem, defaultSelectedPage } = props;
  const [selectedPageId, setSelectedPageId] = useState("");
  useOnChangeWatcher(documentPageGroup);
  const rows = new Array<any>();

  function FloorDropDown(props: { pdfPage: Page | PdfPageReference; onChange: () => void }) {
    const { pdfPage, onChange } = props;
    const [floor, setFloor] = useState<undefined | Floor>();

    const redraw = useRedraw();

    useOnChangeWatcher(pdfPage, () => {
      if (
        floor?.page ||
        floor?.page?.pdfPageRefId != pdfPage.id ||
        (floor?.page?.pdfPageRefId == pdfPage.id && floor?.page?.pageNumber != pdfPage.pageNumber)
      ) {
        setFloor(undefined);
      }
      return true;
    });

    const options = new Array<any>();
    function onSelect(ev: any) {
      const floorId = ev.target.value;
      const floor1 = state.objectRegistry.getObject(floorId) as Floor;
      if (floor1) {
        // set pdf as background
        const floorPage = floor1.getOrCreatePage();

        if (pdfPage.className == "PdfPageReference") {
          const pdfDocument = pdfPage.parent as Page;
          setFloor(floor1);
          floorPage.setBackgroundImageFromBuildingPdf(pdfDocument, (pdfPage as PdfPageReference).pageNumber);
        } else if (pdfPage.className == "Page") {
          setFloor(floor1);
          floorPage.setBackgroundImageFromBuildingPdf(pdfPage as Page, 1);
        }
        pdfPage.raiseOnChange("pdfPageRef");
        floor1.raiseOnChange("backgroundUrl");
      } else {
        floor?.page?.removeBackgroundImage();
        setFloor(undefined);
      }
      onChange();
      redraw();
    }
    // default if no floor uses this file
    options.push(
      <option
        value="empty" // no itx
      >
        <div style={{ color: "lightgrey" }}>{itx("loadFloorPlan.selectFloor", "Etage Auswählen")}</div>
      </option>,
    );

    //// for each flor
    state.floors.forEach((floor) => {
      options.push(
        <option value={floor.id}>
          {floor.tag} {floor.name}
        </option>,
      );
    });

    const defaultValue = (pdfPage as Page).usageParentIds[0] || "empty"; // no itx

    const color = defaultValue == "empty" ? "grey" : undefined;
    const background = defaultValue == "empty" ? "transparent" : "white";
    return (
      <select
        key={defaultValue}
        className="select"
        style={{ background: background, color: color }}
        onChange={onSelect}
        defaultValue={defaultValue}
      >
        {options}
      </select>
    );
  }

  // each pdf  from pageGroup and there children are in this pdf row
  function PdfRow(props: { page: Page; onChange: () => void }) {
    const { page, onChange } = props;
    useOnChangeWatcher(page);
    useOnChangeWatcher(page.parent);
    const list = new Array<Page>();
    const elements = new Array<any>();

    const children = page.treeViewModel.childCollection;
    if (children) {
      children.forEach((child) => {
        list.push(child as Page);
      });
    }

    const styleRow: any = { cursor: "pointer" };
    let pageSelected = selectedPageId == page.id;
    if (selectedPageId == "" && defaultSelectedPage) {
      pageSelected = defaultSelectedPage?.page == page && defaultSelectedPage.pageNumber == 0;
    }

    page.treeViewModel.isFolder; // muss hier stehen, da sonst die oagerefs nicht geladen werden
    function RadioButton(props: { selected: boolean; show: boolean }) {
      const { selected, show } = props;
      return (
        <div style={{ color: selected ? "orange" : "black" }}>
          {show && (
            <i
              className={selected ? "las la-dot-circle" : "las la-circle"} // no itx
            />
          )}
        </div>
      );
    }

    elements.push(
      <tr
        onClick={(ev) => {
          if (selectedElem && list.length == 0) {
            selectedElem({ page: page, pageNumber: page.pageNumber });
            setSelectedPageId(page.id);
            onChange();
          }
        }}
        style={{ ...styleRow }}
        className={!selectedElem || list.length != 0 ? "" : "hoverableText"} // no itx
        key={page.id + "_pdfRow"}
      >
        {selectedElem && (
          <td>
            <RadioButton selected={pageSelected} show={list.length == 0} />
          </td>
        )}
        {
          <td>
            <DeleteButton obj={page} style={{ position: "relative", marginRight: "10px", right: undefined }} />
          </td>
        }
        <td>{page.tag}</td>
        <td>{page.name}</td>

        {!selectedElem && <td>{list.length == 0 && <FloorDropDown onChange={onChange} pdfPage={page} />}</td>}
      </tr>,
    );
    list.forEach((child) => {
      let selected = selectedPageId == child.id;
      if (selectedPageId == "" && defaultSelectedPage) {
        selected = defaultSelectedPage?.page == page && defaultSelectedPage?.pageNumber == child.pageNumber;
        if (!selected) {
          selected = defaultSelectedPage?.page == child && defaultSelectedPage?.pageNumber == child.pageNumber;
        }
      }
      elements.push(
        <tr
          onClick={(ev) => {
            if (selectedElem) {
              selectedElem({ page: child, pageNumber: child.pageNumber });
              setSelectedPageId(child.id);
            }
          }}
          style={{ ...styleRow }}
        >
          {selectedElem && <td></td>}
          <td></td>
          <td
            className={!selectedElem ? "hoverableText" : ""} // no itx
            style={{ paddingLeft: "40px", display: "flex", flexDirection: "row" }}
          >
            {selectedElem && <RadioButton selected={selected} show={true} />}
            {selectedElem && <div style={{ width: "10px" }}></div>}
            {child.name}
          </td>
          {!selectedElem && <td>{<FloorDropDown onChange={onChange} pdfPage={child} />}</td>}
        </tr>,
      );
    });
    return <tbody>{elements}</tbody>;
  }
  documentPageGroup.pages.forEach((page) => {
    rows.push(<PdfRow onChange={onChange} page={page} />);
  });
  if (defaultSelectedPage && rows.length == 0) {
    return (
      <div style={{ color: "grey", height: "100%" }}>
        <br />
        <br />
        {itx("loadFloorPlan.noFloorplansInProject", "Es sind noch keine Etagenpläne in diesem Projekt vorhanden.")}
        <br />
        <br />
        {itx(
          "loadFloorPlan.uploadFloorPlans",
          "Bitte laden sie welche hoch, um eie dieser für diese Etage verwenden zu können!",
        )}
      </div>
    );
  }
  return (
    <table style={{ alignSelf: "baseline" }}>
      <tbody>{rows}</tbody>
    </table>
  );
}
export function LoadNewBuildingPlansButton(props: {
  documentPageGroup: PageGroup | undefined;
  onAnalysisStart?: () => void;
  onAnalysisProgress?: (text: string) => void;
  onAnalysisComplete?: (success: boolean) => void;
}) {
  const { documentPageGroup, onAnalysisStart, onAnalysisProgress, onAnalysisComplete } = props;
  return (
    <div style={{ padding: "0px" }}>
      <label className="button" style={{ paddingTop: "3px" }}>
        <input
          style={{ display: "none" }}
          id="file"
          name="file"
          type="file"
          accept=".pdf"
          onClick={(event) => {
            event.stopPropagation;
          }}
          onChange={async (event) => {
            if (documentPageGroup) {
              const pdfDocument = await (
                documentPageGroup.treeViewModel as PageGroupTreeViewModel
              ).createPageFromPdfFile(event as unknown as ChangeEvent<HTMLInputElement>);
              state.eventLogger.addEvent(EventKey.BackgroundPdfUploaded, {
                pageGroupId: documentPageGroup.id,
              });

              // KI-Erkennung von Etagen/Räumen aus dem hochgeladenen Plan (nur im Assistenten
              // relevant, wo diese Callbacks gesetzt sind – bei sonstigen Verwendungen dieses
              // Buttons, z.B. im laufenden Projekt, bleibt das Verhalten unverändert).
              const pdfFile = event.target.files?.[0];
              if (pdfFile && onAnalysisStart) {
                onAnalysisStart();
                detectFloorsAndRoomsFromFile(pdfFile, pdfDocument, (text) => onAnalysisProgress?.(text)).then(
                  (success) => {
                    onAnalysisComplete?.(success);
                  },
                );
              }
            }
          }}
        />
        {itx("loadFloorPlan.changebuidingPlan", "Gebäudeplan laden")}
        <i className="las la-upload" />
      </label>
    </div>
  );
}
